import {isLoaded, PackageModel, UnassignedPackageModel} from 'ApplicationEntry/models/ProductsRiders/PackageModel';
import {Loaded} from 'Core/models/Async';
import {EmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

type AIPProps = {
  units: StringUnion;
  planType: StringUnion;
  indWeeklyPremium: StringUnion;
  familyWeeklyPremium: StringUnion;
  indMonthlyPremium: StringUnion;
  familyMonthlyPremium: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  section125: boolean;
  packageOption: StringUnion;
  situs: StringUnion;
  jstamp: boolean;
  packageList: PackageModel;
};

export class InvalidOrdinaryAIP {
  public readonly key = 'InvalidOrdinaryAIP';

  constructor(
    public readonly units: StringUnion,
    public readonly planType: StringUnion,
    public readonly indWeeklyPremium: StringUnion,
    public readonly familyWeeklyPremium: StringUnion,
    public readonly indMonthlyPremium: StringUnion,
    public readonly familyMonthlyPremium: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly packageOption: StringUnion,
    public readonly situs: StringUnion,
    public readonly jstamp: boolean,
    public readonly packageList: PackageModel
  ) {}
}

export class ValidOrdinaryAIP {
  public static createApp({
    units,
    planType,
    indWeeklyPremium,
    familyWeeklyPremium,
    indMonthlyPremium,
    familyMonthlyPremium,
    premium,
    calculatedPremium,
    cwa,
    section125,
    packageOption,
    situs,
    jstamp,
    packageList,
  }: AIPProps) {
    return this.create(
      units,
      planType,
      indWeeklyPremium,
      familyWeeklyPremium,
      indMonthlyPremium,
      familyMonthlyPremium,
      premium,
      calculatedPremium,
      cwa,
      section125,
      packageOption,
      situs,
      jstamp,
      packageList
    );
  }

  public static create(
    units: StringUnion,
    planType: StringUnion,
    indWeeklyPremium: StringUnion,
    familyWeeklyPremium: StringUnion,
    indMonthlyPremium: StringUnion,
    familyMonthlyPremium: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    section125: boolean,
    packageOption: StringUnion,
    situs: StringUnion,
    jstamp: boolean,
    packageList: PackageModel
  ) {
    return isLoaded(packageList)
      ? new ValidOrdinaryAIP(
          units,
          planType,
          indWeeklyPremium,
          familyWeeklyPremium,
          indMonthlyPremium,
          familyMonthlyPremium,
          premium,
          calculatedPremium,
          cwa,
          section125,
          packageOption,
          situs,
          jstamp,
          packageList
        )
      : new InvalidOrdinaryAIP(
          units,
          planType,
          indWeeklyPremium,
          familyWeeklyPremium,
          indMonthlyPremium,
          familyMonthlyPremium,
          premium,
          calculatedPremium,
          cwa,
          section125,
          packageOption,
          situs,
          jstamp,
          packageList
        );
  }

  public readonly key = 'ValidOrdinaryAIP';
  constructor(
    public readonly units: StringUnion,
    public readonly planType: StringUnion,
    public readonly indWeeklyPremium: StringUnion,
    public readonly familyWeeklyPremium: StringUnion,
    public readonly indMonthlyPremium: StringUnion,
    public readonly familyMonthlyPremium: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly packageOption: StringUnion,
    public readonly situs: StringUnion,
    public readonly jstamp: boolean,
    public readonly packageList: Loaded<object[]>
  ) {}
}

export type OrdinaryAIP = InvalidOrdinaryAIP | ValidOrdinaryAIP;

export type OrdinaryAIPOptions<TReturn> = ToPartialMatchOptions<OrdinaryAIP, 'key', TReturn>;

export const matchOrdinaryAIP = <TReturn>(options: OrdinaryAIPOptions<TReturn>) => (app: OrdinaryAIP): TReturn => {
  switch (app.key) {
    case 'InvalidOrdinaryAIP':
      return options.InvalidOrdinaryAIP ? options.InvalidOrdinaryAIP(app) : options.default(app);
    case 'ValidOrdinaryAIP':
      return options.ValidOrdinaryAIP ? options.ValidOrdinaryAIP(app) : options.default(app);
  }
};

export const EmptyOrdinaryAIP = new InvalidOrdinaryAIP(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  new EmptyString(),
  new EmptyString(),
  false,
  new UnassignedPackageModel()
);

export const changeUnits = (units: string) => (app: OrdinaryAIP) =>
  ValidOrdinaryAIP.createApp({...app, units: NonEmptyString.create(units)});
export const changePlanType = (planType: string) => (app: OrdinaryAIP) =>
  ValidOrdinaryAIP.createApp({...app, planType: NonEmptyString.create(planType)});
  export const changeIndWeeklyPremium = (indWeeklyPremium: string) => (app: OrdinaryAIP) =>
  ValidOrdinaryAIP.createApp({...app, indWeeklyPremium: NonEmptyString.create(indWeeklyPremium)});
  export const changeFamilyWeeklyPremium = (familyWeeklyPremium: string) => (app: OrdinaryAIP) =>
  ValidOrdinaryAIP.createApp({...app, familyWeeklyPremium: NonEmptyString.create(familyWeeklyPremium)});
  export const changeIndMonthlyPremium = (indMonthlyPremium: string) => (app: OrdinaryAIP) =>
  ValidOrdinaryAIP.createApp({...app, indMonthlyPremium: NonEmptyString.create(indMonthlyPremium)});
  export const changeFamilyMonthlyPremium = (familyMonthlyPremium: string) => (app: OrdinaryAIP) =>
  ValidOrdinaryAIP.createApp({...app, familyMonthlyPremium: NonEmptyString.create(familyMonthlyPremium)});
export const changePremium = (premium: string) => (app: OrdinaryAIP) =>
  ValidOrdinaryAIP.createApp({...app, premium: NonEmptyString.create(premium)});
export const changeCalculatedPremium = (calculatedPremium: string) => (app: OrdinaryAIP) =>
  ValidOrdinaryAIP.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});
export const changeCWA = (cwa: string) => (app: OrdinaryAIP) =>
  ValidOrdinaryAIP.createApp({...app, cwa: NonEmptyString.create(cwa)});
export const changeSection = (section: boolean) => (app: OrdinaryAIP) =>
  ValidOrdinaryAIP.createApp({...app, section125: section});
export const changePackage = (packageOption: string) => (app: OrdinaryAIP) =>
  ValidOrdinaryAIP.createApp({...app, packageOption: NonEmptyString.create(packageOption)});
export const changeSitus = (situs: string) => (app: OrdinaryAIP) =>
  ValidOrdinaryAIP.createApp({...app, situs: NonEmptyString.create(situs)});
export const changeJStamp = (jstamp: boolean) => (app: OrdinaryAIP) => ValidOrdinaryAIP.createApp({...app, jstamp});
export const changePackageList = (packages: PackageModel) => (app: OrdinaryAIP) =>
  ValidOrdinaryAIP.createApp({...app, packageList: packages});
